/**
 * FinanceSection – Zbiorcze zestawienie wypłat (embedded w OwnerDashboard)
 * Lista płac dla wszystkich pracowników za wybrany zakres dat.
 * Obsługuje status "Częściowo Opłacone" i sortowanie tabeli.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '@/contexts/RoleContext';
import { useCanViewOrgFinancials } from '@/hooks/useCanViewOrgFinancials';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DollarSign,
  Loader2,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  Calendar as CalendarIcon,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Wallet,
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale/pl';
import { cn } from '@/lib/utils';

const WORK_LOG_MAX_DISPLAY_HOURS = 16;

type FinanceStatus = 'paid' | 'pending' | 'partial';

interface FinanceRow {
  staffId: string;
  fullName: string;
  totalHours: number;
  totalEarnings: number;
  status: FinanceStatus;
  paidAmount: number | null;
  unpaidAmount: number;
  paidAmountInPeriod: number;
  tooltipText?: string;
  /** Period of payout for paid/partial status (e.g. "Rozliczono: Luty 2026") */
  payoutPeriodText?: string;
}

/** Format staff_payouts month/year as human-readable period (e.g. "Luty 2026") */
function formatPayoutPeriod(month: number, year: number): string {
  const d = new Date(year, month - 1, 1);
  return format(d, 'LLLL yyyy', { locale: pl });
}

type SortKey = 'fullName' | 'totalHours' | 'totalEarnings' | 'status';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

function getDefaultDateRange(): { from: Date; to: Date } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now);
  return { from, to };
}

function getSectionName(task: { property_sections?: { name?: string } | { name?: string }[] | null }): string {
  const ps = task.property_sections;
  if (ps) {
    const name = Array.isArray(ps) ? ps[0]?.name : (ps as { name?: string })?.name;
    if (typeof name === 'string' && name.trim()) return name.trim();
  }
  return '';
}

function isSnowTask(task: {
  property_sections?: { name?: string } | { name?: string }[] | null;
  checklist?: unknown;
}): boolean {
  const sectionName = getSectionName(task);
  if (
    sectionName &&
    (sectionName.toLowerCase().includes('odśnieżanie') ||
      sectionName.toLowerCase().includes('śnieg') ||
      sectionName.toLowerCase().includes('snow'))
  ) {
    return true;
  }
  const checklist = task.checklist as { name?: string; task_name?: string }[] | Record<string, { name?: string; task_name?: string }> | null;
  if (!checklist) return false;
  const arr = Array.isArray(checklist) ? checklist : Object.values(checklist);
  for (const item of arr) {
    const name = item?.name || item?.task_name || '';
    if (
      name &&
      (name.toLowerCase().includes('odśnieżanie') ||
        name.toLowerCase().includes('śnieg') ||
        name.toLowerCase().includes('snow'))
    ) {
      return true;
    }
  }
  return false;
}

function calcTaskEarnings(
  task: {
    started_at: string | null;
    completed_at: string | null;
    total_cost: number | null;
    task_type?: string | null;
    property_sections?: unknown;
    checklist?: unknown;
  },
  baseRate: number,
  snowRate: number
): number {
  if (!task.started_at || !task.completed_at) return 0;
  const taskTotalCost = task.total_cost != null ? parseFloat(String(task.total_cost)) : null;
  const useSnapshot = taskTotalCost != null && taskTotalCost > 0;
  if (task.task_type === 'extra_paid' && !useSnapshot) return 0;
  const start = new Date(task.started_at);
  const end = new Date(task.completed_at);
  const rawHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  const hours = Math.min(rawHours, WORK_LOG_MAX_DISPLAY_HOURS);
  const snow = isSnowTask(task);
  return useSnapshot
    ? taskTotalCost!
    : snow
      ? hours * snowRate
      : hours * baseRate;
}

export function FinanceSection() {
  const { currentOrgId } = useRole();
  const canViewOrgFinancials = useCanViewOrgFinancials();
  const navigate = useNavigate();
  const defaultRange = useMemo(getDefaultDateRange, []);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(defaultRange);
  const [rows, setRows] = useState<FinanceRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'fullName', direction: 'asc' });
  const [payoutInProgressId, setPayoutInProgressId] = useState<string | null>(null);

  const canEdit = canViewOrgFinancials;

  const fetchFinanceData = useCallback(async () => {
    if (!currentOrgId || !canViewOrgFinancials) return;

    setIsLoading(true);
    try {
      const from = new Date(dateRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(dateRange.to);
      to.setHours(23, 59, 59, 999);
      const fromISO = from.toISOString();
      const toISO = to.toISOString();

      const { data: membersData, error: membersError } = await supabase
        .from('memberships')
        .select('user_id, role')
        .eq('org_id', currentOrgId)
        .in('role', ['cleaner', 'staff'])
        .eq('is_active', true);

      if (membersError) {
        console.error('Error fetching members:', membersError);
        toast.error('Błąd pobierania pracowników');
        setRows([]);
        return;
      }

      const staffIds = Array.from(
        new Set((membersData || []).map((m) => m.user_id).filter(Boolean))
      ) as string[];

      if (staffIds.length === 0) {
        setRows([]);
        setIsLoading(false);
        return;
      }

      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, full_name, base_hourly_rate, snow_hourly_rate, fixed_salary')
        .in('id', staffIds);

      const profilesMap = new Map(
        (profilesData || []).map((p) => [
          p.id,
          {
            full_name: p.full_name || 'Brak nazwiska',
            base_hourly_rate: p.base_hourly_rate ?? 0,
            snow_hourly_rate: p.snow_hourly_rate ?? 0,
            fixed_salary: p.fixed_salary ?? 0,
          },
        ])
      );

      const { data: tasksData, error: tasksError } = await supabase
        .from('cleaning_tasks')
        .select(
          'id, assigned_staff_id, started_at, completed_at, total_cost, is_paid, applied_hourly_rate, task_type, checklist, property_sections(name)'
        )
        .in('assigned_staff_id', staffIds)
        .eq('status', 'done')
        .eq('org_id', currentOrgId)
        .gte('started_at', fromISO)
        .lte('started_at', toISO);

      if (tasksError) {
        console.error('Error fetching tasks:', tasksError);
        toast.error('Błąd pobierania zadań');
        setRows([]);
        return;
      }

      const { data: sessionsData } = await supabase
        .from('cleaning_work_sessions')
        .select('user_id, started_at, ended_at, session_kind')
        .eq('org_id', currentOrgId)
        .in('user_id', staffIds)
        .neq('status', 'open')
        .not('ended_at', 'is', null)
        .gte('started_at', fromISO)
        .lte('started_at', toISO);

      const { data: adjustmentsData } = await supabase
        .from('staff_financial_adjustments')
        .select('user_id, amount')
        .in('user_id', staffIds)
        .gte('created_at', fromISO)
        .lte('created_at', toISO);

      const adjustmentsByStaff = new Map<string, number>();
      (adjustmentsData || []).forEach((adj) => {
        const id = adj.user_id;
        const amt = parseFloat(String(adj.amount));
        adjustmentsByStaff.set(id, (adjustmentsByStaff.get(id) || 0) + amt);
      });

      // Fetch staff_payouts for months overlapping with date range
      const { data: payoutsData } = await supabase
        .from('staff_payouts')
        .select('staff_id, month, year')
        .eq('org_id', currentOrgId)
        .in('staff_id', staffIds);

      const payoutsByStaff = new Map<string, { month: number; year: number }[]>();
      (payoutsData || []).forEach((p) => {
        const payoutStart = new Date(p.year, p.month - 1, 1);
        const payoutEnd = new Date(p.year, p.month, 0, 23, 59, 59);
        const overlaps = payoutStart <= to && payoutEnd >= from;
        if (overlaps) {
          const list = payoutsByStaff.get(p.staff_id) ?? [];
          list.push({ month: p.month, year: p.year });
          payoutsByStaff.set(p.staff_id, list);
        }
      });

      // Count months overlapping with date range (full fixed salary per month, no prorate guard)
      const monthsInRange: number[] = [];
      let d = new Date(from);
      d.setDate(1);
      while (d <= to) {
        monthsInRange.push(d.getTime());
        d.setMonth(d.getMonth() + 1);
      }
      const monthCount = monthsInRange.length;

      const result: FinanceRow[] = staffIds.map((staffId) => {
        const profile = profilesMap.get(staffId);
        const baseRate = profile?.base_hourly_rate ?? 0;
        const snowRate = profile?.snow_hourly_rate ?? 0;
        // Full fixed salary per month in range (owner can pay before month end)
        const fixedSalary = (profile?.fixed_salary ?? 0) * Math.max(1, monthCount);
        const adj = adjustmentsByStaff.get(staffId) || 0;

        const staffTasks = (tasksData || []).filter((t) => t.assigned_staff_id === staffId);
        const paidTasks = staffTasks.filter((t) => t.is_paid === true);
        const unpaidTasks = staffTasks.filter((t) => t.is_paid !== true);

        let totalHours = 0;
        let paidAmountInPeriod = 0;
        let unpaidAmount = 0;

        paidTasks.forEach((task) => {
          const extraPending = task.task_type === 'extra_paid' && !(task.total_cost != null && parseFloat(String(task.total_cost)) > 0);
          if (extraPending) return;
          if (task.started_at && task.completed_at) {
            const start = new Date(task.started_at);
            const end = new Date(task.completed_at);
            const rawHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
            totalHours += Math.min(rawHours, WORK_LOG_MAX_DISPLAY_HOURS);
          }
          paidAmountInPeriod += calcTaskEarnings(task, baseRate, snowRate);
        });

        unpaidTasks.forEach((task) => {
          const extraPending = task.task_type === 'extra_paid' && !(task.total_cost != null && parseFloat(String(task.total_cost)) > 0);
          if (extraPending) return;
          if (task.started_at && task.completed_at) {
            const start = new Date(task.started_at);
            const end = new Date(task.completed_at);
            const rawHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
            totalHours += Math.min(rawHours, WORK_LOG_MAX_DISPLAY_HOURS);
          }
          unpaidAmount += calcTaskEarnings(task, baseRate, snowRate);
        });

        const staffSessions = (sessionsData || []).filter((session) => session.user_id === staffId);
        let sessionHours = 0;
        staffSessions.forEach((session) => {
          if (!session.started_at || !session.ended_at) return;
          if (session.session_kind === 'extra_paid') return;
          const rawHours = (new Date(session.ended_at).getTime() - new Date(session.started_at).getTime()) / (1000 * 60 * 60);
          sessionHours += Math.min(rawHours, WORK_LOG_MAX_DISPLAY_HOURS);
        });
        if (sessionHours > totalHours) {
          unpaidAmount += (sessionHours - totalHours) * baseRate;
          totalHours = sessionHours;
        }

        unpaidAmount += fixedSalary + adj;

        let status: FinanceStatus;
        let tooltipText: string | undefined;

        if (unpaidTasks.length === 0 && paidTasks.length > 0) {
          status = 'paid';
          tooltipText = `Opłacono już: ${(paidAmountInPeriod + fixedSalary + adj).toFixed(2)} zł`;
        } else if (paidTasks.length === 0 && unpaidTasks.length > 0) {
          status = 'pending';
        } else if (paidTasks.length > 0 && unpaidTasks.length > 0) {
          status = 'partial';
          tooltipText = `Opłacono już: ${paidAmountInPeriod.toFixed(2)} zł, Pozostało: ${unpaidAmount.toFixed(2)} zł`;
        } else {
          status = 'pending';
        }

        const payoutPeriods = payoutsByStaff.get(staffId) ?? [];
        payoutPeriods.sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
        const payoutPeriodText =
          (status === 'paid' || status === 'partial') && payoutPeriods.length > 0
            ? payoutPeriods.length === 1
              ? `Rozliczono: ${formatPayoutPeriod(payoutPeriods[0].month, payoutPeriods[0].year)}`
              : `Rozliczono: ${payoutPeriods.map((p) => formatPayoutPeriod(p.month, p.year)).join(', ')}`
            : undefined;

        return {
          staffId,
          fullName: profile?.full_name ?? 'Brak nazwiska',
          totalHours: parseFloat(totalHours.toFixed(2)),
          totalEarnings: parseFloat((paidAmountInPeriod + unpaidAmount).toFixed(2)),
          status,
          paidAmount: status === 'paid' ? paidAmountInPeriod + fixedSalary + adj : null,
          unpaidAmount: parseFloat(unpaidAmount.toFixed(2)),
          paidAmountInPeriod: parseFloat(paidAmountInPeriod.toFixed(2)),
          tooltipText,
          payoutPeriodText,
        };
      });

      setRows(result);
    } catch (err) {
      console.error('FinanceSection fetch error:', err);
      toast.error('Wystąpił błąd podczas pobierania danych');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrgId, dateRange.from, dateRange.to, canViewOrgFinancials]);

  useEffect(() => {
    fetchFinanceData();
  }, [fetchFinanceData]);

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectable = rows.filter((r) => r.status === 'pending' || r.status === 'partial');
      setSelectedIds(new Set(selectable.map((r) => r.staffId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const executePayoutForStaff = useCallback(
    async (staffId: string, unpaidAmount: number): Promise<boolean> => {
      const from = new Date(dateRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(dateRange.to);
      to.setHours(23, 59, 59, 999);
      const fromISO = from.toISOString();
      const toISO = to.toISOString();

      const { data: userData } = await supabase.auth.getUser();
      const paidBy = userData.user?.id || null;

      const { error } = await supabase.from('staff_payouts').insert({
        staff_id: staffId,
        org_id: currentOrgId,
        month: dateRange.to.getMonth() + 1,
        year: dateRange.to.getFullYear(),
        total_amount: unpaidAmount,
        paid_by: paidBy,
      });

      if (error) {
        console.error('Error marking as paid for', staffId, error);
        return false;
      }

      const { data: unpaidTasks, error: tasksError } = await supabase
        .from('cleaning_tasks')
        .select('id')
        .eq('assigned_staff_id', staffId)
        .eq('status', 'done')
        .eq('org_id', currentOrgId)
        .or('is_paid.is.null,is_paid.eq.false')
        .gte('started_at', fromISO)
        .lte('started_at', toISO);

      if (!tasksError && unpaidTasks && unpaidTasks.length > 0) {
        const taskIds = unpaidTasks.map((t) => t.id);
        await supabase.from('cleaning_tasks').update({ is_paid: true }).in('id', taskIds);
      }
      return true;
    },
    [currentOrgId, dateRange.from, dateRange.to]
  );

  const handleMarkSelectedAsPaid = async () => {
    if (!currentOrgId || staffToPay.length === 0) return;

    setIsMarkingAsPaid(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const row of staffToPay) {
        const ok = await executePayoutForStaff(row.staffId, row.unpaidAmount);
        if (ok) successCount++;
        else errorCount++;
      }

      if (successCount > 0) {
        toast.success(`Rozliczono poprawnie: ${successCount} pracowników`);
        setSelectedIds(new Set());
        await fetchFinanceData();
      }
      if (errorCount > 0) {
        toast.error(`Nie udało się dla ${errorCount} pracowników`);
      }
    } catch (err) {
      console.error('Bulk mark as paid error:', err);
      toast.error('Wystąpił błąd');
    } finally {
      setIsMarkingAsPaid(false);
    }
  };

  const handleMarkSingleAsPaid = async (staffId: string) => {
    const row = rows.find((r) => r.staffId === staffId);
    if (!row || row.unpaidAmount <= 0 || !currentOrgId) return;

    setPayoutInProgressId(staffId);
    try {
      const ok = await executePayoutForStaff(staffId, row.unpaidAmount);
      if (ok) {
        toast.success('Rozliczono poprawnie');
        await fetchFinanceData();
      } else {
        toast.error('Nie udało się rozliczyć');
      }
    } catch (err) {
      console.error('Single payout error:', err);
      toast.error('Wystąpił błąd');
    } finally {
      setPayoutInProgressId(null);
    }
  };

  const selectableCount = rows.filter((r) => r.status === 'pending' || r.status === 'partial').length;
  const staffToPay =
    selectedIds.size > 0
      ? rows.filter(
          (r) =>
            selectedIds.has(r.staffId) &&
            (r.status === 'pending' || r.status === 'partial') &&
            r.unpaidAmount > 0
        )
      : rows.filter((r) => (r.status === 'pending' || r.status === 'partial') && r.unpaidAmount > 0);
  const canBulkMark = canEdit && staffToPay.length > 0;

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortConfig.key) {
        case 'fullName':
          cmp = a.fullName.localeCompare(b.fullName);
          break;
        case 'totalHours':
          cmp = a.totalHours - b.totalHours;
          break;
        case 'totalEarnings':
          cmp = a.unpaidAmount - b.unpaidAmount;
          break;
        case 'status':
          cmp = (a.status === 'paid' ? 3 : a.status === 'partial' ? 2 : 1) - (b.status === 'paid' ? 3 : b.status === 'partial' ? 2 : 1);
          break;
        default:
          cmp = 0;
      }
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-1 h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="ml-1 h-3.5 w-3.5" />
    );
  };

  const fromStr = format(dateRange.from, 'yyyy-MM-dd');
  const toStr = format(dateRange.to, 'yyyy-MM-dd');

  if (!canViewOrgFinancials) {
    return (
      <p className="text-sm text-muted-foreground">
        Brak uprawnień do podglądu finansów organizacji.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-emerald-600" />
          Finanse / Lista Płac
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Zbiorcze zestawienie wypłat za wybrany zakres dat
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle>Zestawienie za okres</CardTitle>
              <CardDescription>
                Wybierz zakres dat, aby zobaczyć należności pracowników
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-[280px] justify-start text-left font-normal',
                      !dateRange.from && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'dd MMM yyyy', { locale: pl })} -{' '}
                          {format(dateRange.to, 'dd MMM yyyy', { locale: pl })}
                        </>
                      ) : (
                        format(dateRange.from, 'dd MMM yyyy', { locale: pl })
                      )
                    ) : (
                      <span>Wybierz zakres dat</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => {
                      if (range?.from) {
                        setDateRange({
                          from: range.from,
                          to: range.to ?? range.from,
                        });
                      }
                    }}
                    numberOfMonths={2}
                    locale={pl}
                  />
                </PopoverContent>
              </Popover>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDateRange(getDefaultDateRange())}
              >
                <X className="mr-2 h-4 w-4" />
                Reset
              </Button>
              <Button variant="outline" size="icon" onClick={fetchFinanceData} disabled={isLoading}>
                <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {canEdit && selectableCount > 0 && (
            <div className="flex flex-wrap items-center gap-4 mb-4 p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="select-all-pending"
                  checked={
                    selectableCount > 0 &&
                    rows
                      .filter((r) => r.status === 'pending' || r.status === 'partial')
                      .every((r) => selectedIds.has(r.staffId))
                  }
                  onCheckedChange={(c) => handleSelectAll(!!c)}
                />
                <label htmlFor="select-all-pending" className="text-sm font-medium cursor-pointer">
                  Zaznacz wszystkich oczekujących ({selectableCount})
                </label>
              </div>
              <Button
                size="default"
                onClick={handleMarkSelectedAsPaid}
                disabled={!canBulkMark || isMarkingAsPaid}
                className="gap-2"
              >
                {isMarkingAsPaid ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Zatwierdź Wypłaty
                {selectedIds.size > 0
                  ? ` (${selectedIds.size})`
                  : ` (wszyscy: ${staffToPay.length})`}
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              Brak pracowników w tej organizacji lub brak danych dla wybranego okresu.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canEdit && <TableHead className="w-[50px]"></TableHead>}
                  <TableHead
                    className="bg-slate-50 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700"
                    onClick={() => handleSort('fullName')}
                  >
                    <span className="flex items-center">
                      Pracownik
                      <SortIcon columnKey="fullName" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="bg-slate-50 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700"
                    onClick={() => handleSort('totalHours')}
                  >
                    <span className="flex items-center">
                      Suma godzin
                      <SortIcon columnKey="totalHours" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="bg-slate-50 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700"
                    onClick={() => handleSort('totalEarnings')}
                  >
                    <span className="flex items-center">
                      Do Wypłaty
                      <SortIcon columnKey="totalEarnings" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="bg-slate-50 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700"
                    onClick={() => handleSort('status')}
                  >
                    <span className="flex items-center">
                      Status
                      <SortIcon columnKey="status" />
                    </span>
                  </TableHead>
                  <TableHead className="bg-slate-50 dark:bg-slate-800 w-[100px]">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((row) => {
                  const initials = row.fullName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || '?';
                  const amountCell = (
                    <span className="font-semibold">
                      {row.status === 'paid' && row.paidAmount != null
                        ? `${row.paidAmount.toFixed(2)} zł`
                        : `${row.unpaidAmount.toFixed(2)} zł`}
                    </span>
                  );
                  return (
                    <TableRow key={row.staffId}>
                      {canEdit && (
                        <TableCell>
                          {row.status === 'pending' || row.status === 'partial' ? (
                            <Checkbox
                              checked={selectedIds.has(row.staffId)}
                              onCheckedChange={() => handleToggleSelect(row.staffId)}
                              disabled={row.unpaidAmount <= 0}
                            />
                          ) : (
                            <span className="inline-block w-4" />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{row.fullName}</span>
                        </div>
                      </TableCell>
                      <TableCell>{row.totalHours.toFixed(2)} h</TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {row.tooltipText ? (
                                <span className="cursor-help">{amountCell}</span>
                              ) : (
                                amountCell
                              )}
                            </TooltipTrigger>
                            {row.tooltipText && (
                              <TooltipContent>
                                <p>{row.tooltipText}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          {row.status === 'paid' ? (
                            <Badge className="bg-green-600 w-fit">Opłacone</Badge>
                          ) : row.status === 'partial' ? (
                            <Badge className="bg-amber-600 w-fit">Częściowo Opłacone</Badge>
                          ) : (
                            <Badge variant="secondary" className="w-fit">Oczekuje</Badge>
                          )}
                          {row.payoutPeriodText && (
                            <span className="text-xs text-muted-foreground">
                              {row.payoutPeriodText}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {(row.status === 'pending' || row.status === 'partial') &&
                            row.unpaidAmount > 0 &&
                            canEdit && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                                      onClick={() => handleMarkSingleAsPaid(row.staffId)}
                                      disabled={payoutInProgressId === row.staffId || isMarkingAsPaid}
                                    >
                                      {payoutInProgressId === row.staffId ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Wallet className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Rozlicz</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                            onClick={() =>
                              navigate(
                                `/workers/${row.staffId}?tab=payroll&from=${fromStr}&to=${toStr}`
                              )
                            }
                          >
                            <ExternalLink className="h-4 w-4" />
                            Szczegóły
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
